import {
  defineWebComponent,
  JBBaseComponent,
  createInputEvent,
  createKeyboardEvent,
  createFocusEvent,
  listenAndSilentEvent,
  isMobile,
  enToFaDigits,
  faToEnDigits,
  parseBooleanAttribute,
} from "jb-core";
import CSS from "./jb-date-input.css";
import VariablesCSS from "./variables.css";
import "jb-calendar";
import "jb-input";
import "jb-popover";
// eslint-disable-next-line no-duplicate-imports
import type { InputType, JBCalendarWebComponent } from "jb-calendar";
import type { JBFormInputStandards } from "jb-form";
import { JBInputWebComponent } from "jb-input";
import { ValidationHelper, type ValidationResult, type ValidationItem, type WithValidation, type ShowValidationErrorParameters } from "jb-validation";
import { registerDefaultVariables } from "jb-core/theme";
import { ValueTypes, type ElementsObject, type DateRestrictions, type ValueType, type ValidationValue, type JBCalendarValue } from "./types.js";
import { DateFactory } from "./date-factory.js";
import {
  checkMaxValidation,
  checkMinValidation,
  getDay,
  getEmptyValueObject,
  getMonth,
  getYear,
  handleBeforeInput,
  getFixedCaretPos,
  emptyInputValueString,
  inputFormat,
  inputRegex,
  getSelectionPart,
} from "jb-date-input/module";
import { requiredValidation } from "./validations.js";
import { renderHTML } from "./render.js";
import { InputTypes, type JBDateInputValueObject } from "jb-date-input/module";
import { dictionary } from "./i18n";
import { i18n } from "jb-core/i18n";

export * from "./types.js";
//headless usage exports
export { handleBeforeInput, emptyInputValueString, getFixedCaretPos, InputTypes, type JBDateInputValueObject, type InputType, dictionary };

//TODO: refactor date-input to use Temporal value as a core value so date object could be filled even with incomplete value
//TODO: add showPicker method for html standard https://web-platform-dx.github.io/web-features-explorer/features/show-picker-input/
export class JBDateInputWebComponent extends JBBaseComponent implements WithValidation<ValidationValue>, JBFormInputStandards<string | null> {
  static formAssociated = true;
  #internals?: ElementInternals;
  elements!: ElementsObject;
  #validation = new ValidationHelper<ValidationValue>({
    clearValidationError: this.clearValidationError.bind(this),
    getValue: () => this.#validationValue,
    getValidations: this.#getInsideValidations.bind(this),
    getValueString: val => val.text,
    setValidationResult: this.#setValidationResult.bind(this),
    showValidationError: this.showValidationError.bind(this),
  });
  #isAutoValidationDisabled = false;
  /**
   * component internal validation system will be disabled so you can control validation by yourself
   */
  get isAutoValidationDisabled(): boolean {
    return this.#isAutoValidationDisabled;
  }
  /**
   * component internal validation system will be disabled so you can control validation by yourself
   */
  set isAutoValidationDisabled(value: boolean) {
    this.#isAutoValidationDisabled = value;
  }
  #dateFactory: DateFactory = new DateFactory({ inputType: this.getAttribute("input-type") as InputTypes, valueType: this.getAttribute("value-type") as ValueTypes });
  #isOpen = false;
  /**
   * jb date input internal validation mechanism works with
   */
  get validation() {
    return this.#validation;
  }
  /**
   * create a restriction for Dates user can input or select
   */
  dateRestrictions: DateRestrictions = {
    min: null,
    max: null,
  };
  #disabled = false;
  get disabled() {
    return this.#disabled;
  }
  set disabled(value: boolean) {
    this.#disabled = value;
    this.elements.input.disabled = value;
    this.elements.calendarTriggerButton.disabled = value;
    if (value) {
      this.#internals?.states?.add("disabled");
      this.#internals!.ariaDisabled = "true";
    } else {
      this.#internals?.states?.delete("disabled");
      this.#internals!.ariaDisabled = "false";
    }
  }
  /**
   * return start position of the selected text
   */
  get selectionStart(): number | null {
    return this.elements.input.selectionStart;
  }
  /**
   * set start position of the selected text
   */
  set selectionStart(value: number | null) {
    this.elements.input.selectionStart = value ?? 0;
  }
  /**
   * return end position of the selected text
   */
  get selectionEnd(): number | null {
    return this.elements.input.selectionEnd;
  }
  /**
   * set end position of the selected text
   */
  set selectionEnd(value: number | null) {
    this.elements.input.selectionEnd = value ?? 0;
  }
  /**
   * return the user selection direction
   */
  get selectionDirection(): "forward" | "backward" | "none" | null {
    return this.elements.input.selectionDirection;
  }
  /**
   * set the user selection direction
   */
  set selectionDirection(value: "forward" | "backward" | "none" | null) {
    this.elements.input.selectionDirection = value ?? "none";
  }
  /**
   * set input selection
   * @param start start of the selection
   * @param end end of the selection
   * @param direction direction of the selection
   */
  setSelectionRange(start: number | null, end: number | null, direction?: "forward" | "backward" | "none") {
    this.elements.input.setSelectionRange(start, end, direction);
  }
  #required = false;
  /**
   * if set true validation failed when user dont fill date completely
   */
  set required(value: boolean) {
    this.#required = value;
    this.#internals!.ariaRequired = value ? "true" : "false";
    this.#checkValidity(false);
  }
  get required() {
    return this.#required;
  }
  #valueObject: JBDateInputValueObject = getEmptyValueObject();
  get name(): string {
    return this.getAttribute("name") || "";
  }
  set name(value: string | null | undefined) {
    if (value) {
      this.setAttribute("name", value);
    } else {
      this.removeAttribute("name");
    }
  }
  get form() {
    return this.#internals!.form;
  }
  get value(): string {
    const value = this.getDateValue();
    return value;
  }
  set value(value: string | Date | null) {
    // Parsing failures leave the live date unchanged, so they must not prevent
    // a later initialValue from initializing this still-clean component.
    if (this.#isAcceptedDateValue(value)) {
      this.#isDirty = true;
    }
    this.#setDateValue(value);
    this.#updateInputTextFromValue();
  }
  #isAcceptedDateValue(value: string | Date | null): boolean {
    if (value === null || value === "") {
      return true;
    }
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }
    if (this.#dateFactory.valueType === "TIME_STAMP") {
      return Number.isFinite(Number(value));
    }
    const parsedValue = this.#dateFactory.getDateObjectValueBaseOnFormat(value);
    return Boolean(parsedValue.year && parsedValue.month && parsedValue.day);
  }
  //set an empty date value as a default initial value
  #initialValue: string | null = null;
  // Tracks whether the live value has been explicitly set. This is separate
  // from the public isDirty comparison against initialValue.
  #isDirty = false;
  get initialValue() {
    return this.#initialValue;
  }
  set initialValue(value: string | null) {
    this.#initialValue = value ?? null;
    if (!this.#isDirty) {
      this.#setDateValue(this.#initialValue);
      this.#updateInputTextFromValue();
    }
  }
  reset() {
    this.#isDirty = false;
    this.#setDateValue(this.initialValue);
    this.#updateInputTextFromValue();
    this.#validation.reset();
    this.elements.input.validation.reset();
    this.#internals?.setValidity({}, "");
  }
  formResetCallback() {
    this.reset();
  }
  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }
  get isDirty() {
    //when initial value is null mean we calculate and build value string base on format, value type , etc on every check to make sure is dirty works well on empty value in every scenario
    return this.value !== (this.initialValue ?? this.#dateFactory.getDateValueStringFromValueObject(getEmptyValueObject(), this.valueType));
  }
  get #validationValue(): ValidationValue {
    return {
      inputObject: this.#dateFactory.getDateObjectValueBaseOnFormat(this.#sInputValue, inputFormat),
      text: this.#sInputValue,
      valueText: this.value,
      valueObject: this.#valueObject,
    };
  }

  setMonthList(inputType: InputType, monthName: string[]) {
    this.elements.calendar.setMonthList(inputType, monthName);
  }
  #updateFormValue(): void {
    //in html form we need to get date input value in native way this function update and set value of the input so form can get it when needed
    if (this.#internals && typeof this.#internals.setFormValue == "function") {
      this.#internals.setFormValue(this.value);
    }
  }
  /**
   * @description return date value if value valid and return null if inputted value is not valid
   */
  get valueAsDate(): Date | null {
    return this.#dateFactory.getDateValueFromValueObject(this.#valueObject);
  }
  get displayValue() {
    return this.#inputValue;
  }
  #placeholder: string | null = null;
  get placeholder() {
    return this.#placeholder;
  }
  set placeholder(value: string | null) {
    this.#placeholder = value;
    this.#internals!.ariaPlaceholder = value;
    if (value !== null) {
      this.elements.input.setAttribute("placeholder", value);
    } else {
      this.elements.input.removeAttribute("placeholder");
    }
    this.#updateInputTextFromValue();
  }
  //standardized input value
  get #sInputValue(): string {
    let value = this.#inputValue;
    if (this.#showPersianNumber) {
      value = faToEnDigits(value);
    }
    return value;
  }
  get #inputValue() {
    return this.elements.input.value;
  }
  set #inputValue(value: string) {
    // it may be called before input is even initialized.
    if (this.elements.input.value !== undefined) {
      this.elements.input.value = value;
    }
  }
  get isOpen(): boolean {
    return this.#isOpen;
  }

  #refreshCalendarDirection() {
    if (typeof this.elements.calendar.refreshDirection === "function") {
      this.elements.calendar.refreshDirection();
      return;
    }
    void customElements.whenDefined("jb-calendar").then(() => {
      this.elements.calendar.refreshDirection();
    });
  }

  set isOpen(value: boolean) {
    this.#isOpen = value;
    if (value == true) {
      // Re-read inherited/CSS direction each time the picker opens.
      this.#refreshCalendarDirection();
      this.#updateCalendarView();
      this.elements.popover.open();
      this.elements.calendarTriggerButton.classList.add("--active");
      this.elements.calendarTriggerButton.setAttribute("aria-expanded", "true");
      if (this.#internals) this.#internals.ariaExpanded = "true";
    } else {
      this.elements.popover.close();
      this.elements.calendarTriggerButton.classList.remove("--active");
      this.elements.calendarTriggerButton.setAttribute("aria-expanded", "false");
      if (this.#internals) this.#internals.ariaExpanded = "false";
      // will reset calendar value to seated value of date-input
      this.#updateCalendarView();
    }
  }

  open(): void {
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
  }

  get inputType(): InputType {
    return this.#dateFactory.inputType;
  }
  #hasInputTypeOverride = false;
  set inputType(value: InputType) {
    this.#hasInputTypeOverride = true;
    this.#setInputType(value);
  }
  #setInputType(value: InputType) {
    if (Object.values(InputTypes).includes(value as InputTypes)) {
      this.#dateFactory.setInputType(value);
      this.onInputTypeChange();
    } else {
      console.error(`${value} is not a valid input type`);
    }
  }
  get valueType() {
    return this.#dateFactory.valueType;
  }
  set valueType(value: ValueType) {
    if (Object.values(ValueTypes).includes(value as ValueTypes)) {
      this.#dateFactory.setValueType(value);
    } else {
      console.error(`${value} is not a valid value type`);
    }
  }
  get yearValue(): number | null {
    switch (this.valueType) {
      case "JALALI":
        return this.#valueObject.jalali.year;
      case "GREGORIAN":
        return this.#valueObject.gregorian.year;
      case "TIME_STAMP":
        return this.#valueObject.gregorian.year;
      default:
        return null;
    }
  }
  get yearDisplayValue(): number | null {
    switch (this.inputType) {
      case "JALALI":
        return this.#valueObject.jalali.year;
      case "GREGORIAN":
        return this.#valueObject.gregorian.year;
      default:
        return null;
    }
  }
  get monthValue(): number | null {
    switch (this.valueType) {
      case "JALALI":
        return this.#valueObject.jalali.month;
      case "GREGORIAN":
        return this.#valueObject.gregorian.month;
      case "TIME_STAMP":
        return this.#valueObject.gregorian.month;
      default:
        return null;
    }
  }
  get monthDisplayValue(): number | null {
    switch (this.inputType) {
      case "JALALI":
        return this.#valueObject.jalali.month;
      case "GREGORIAN":
        return this.#valueObject.gregorian.month;
      default:
        return null;
    }
  }
  get dayValue(): number | null {
    switch (this.valueType) {
      case "JALALI":
        return this.#valueObject.jalali.day;
      case "GREGORIAN":
        return this.#valueObject.gregorian.day;
      case "TIME_STAMP":
        return this.#valueObject.gregorian.day;
      default:
        return null;
    }
  }
  get dayDisplayValue(): number | null {
    switch (this.inputType) {
      case "JALALI":
        return this.#valueObject.jalali.day;
      case "GREGORIAN":
        return this.#valueObject.gregorian.day;
      default:
        return null;
    }
  }
  get yearBaseOnInputType(): number | null {
    switch (this.inputType) {
      case InputTypes.jalali:
        return this.#valueObject.jalali.year;
      case InputTypes.gregorian:
        return this.#valueObject.gregorian.year;
      default:
        return null;
    }
  }
  get monthBaseOnInputType(): number | null {
    switch (this.inputType) {
      case InputTypes.jalali:
        return this.#valueObject.jalali.month;
      case InputTypes.gregorian:
        return this.#valueObject.gregorian.month;
      default:
        return null;
    }
  }
  get dayBaseOnInputType(): number | null {
    switch (this.inputType) {
      case InputTypes.jalali:
        return this.#valueObject.jalali.day;
      case InputTypes.gregorian:
        return this.#valueObject.gregorian.day;
      default:
        return null;
    }
  }
  get typedYear(): string {
    return getYear(this.displayValue);
  }
  get typedMonth(): string {
    return getMonth(this.displayValue);
  }
  get typedDay(): string {
    return getDay(this.displayValue);
  }
  get sTypedYear(): string {
    return getYear(this.#sInputValue);
  }
  get sTypedMonth(): string {
    return getMonth(this.#sInputValue);
  }
  get sTypedDay(): string {
    return getDay(this.#sInputValue);
  }
  get valueFormat() {
    return this.#dateFactory.valueFormat;
  }
  #showPersianNumber = i18n.locale.numberingSystem == "arabext";
  #hasShowPersianNumberOverride = false;
  #unsubscribeLocaleChange: VoidFunction | null = null;
  get showPersianNumber() {
    return this.#showPersianNumber;
  }
  set showPersianNumber(value) {
    this.#hasShowPersianNumberOverride = true;
    this.#setShowPersianNumber(value);
  }
  #setShowPersianNumber(value: boolean) {
    this.#showPersianNumber = value;
    this.elements.calendar.showPersianNumber = value;
    this.#updateInputTextFromValue();
  }
  constructor() {
    super();
    if (typeof this.attachInternals == "function") {
      //some browser dont support attachInternals
      this.#internals = this.attachInternals();
      this.#internals.role = "combobox";
      this.#internals.ariaHasPopup = "dialog";
      this.#internals.ariaExpanded = "false";
    }
    this.#initWebComponent();
    // js standard input element to more associate it with form element
  }
  connectedCallback() {
    // standard web component event that called when all of dom is bounded
    this.#callOnLoadEvent();
    this.#initProp();
  }
  disconnectedCallback() {
    this.#unsubscribeLocaleChange?.();
    this.#unsubscribeLocaleChange = null;
  }
  #callOnLoadEvent() {
    const event = new CustomEvent("load", { bubbles: true, composed: false });
    this.dispatchEvent(event);
  }
  #callOnInitEvent() {
    const event = new CustomEvent("init", { bubbles: true, composed: false });
    this.dispatchEvent(event);
  }
  #initWebComponent() {
    const shadowRoot = this.attachShadow({
      mode: "open",
      delegatesFocus: true,
      serializable: true,
      clonable: true,
    });
    registerDefaultVariables();
    const html = `<style>${CSS} ${VariablesCSS}</style>\n${renderHTML()}`;
    const element = document.createElement("template");
    element.innerHTML = html;
    shadowRoot.appendChild(element.content.cloneNode(true));
    this.elements = {
      input: shadowRoot.querySelector("jb-input")!,
      calendarTriggerButton: shadowRoot.querySelector(".calendar-trigger")!,
      calendar: shadowRoot.querySelector("jb-calendar")!,
      popover: shadowRoot.querySelector("jb-popover")!,
    };
    this.#registerEventListener();
    this.#initDeviceSpecifics();
  }
  /**
   * @description activate some features specially on mobile or other specific devices
   */
  #initDeviceSpecifics() {
    if (isMobile()) {
      // on mobile
      this.elements.input.setAttribute("readonly", "true");
      //TODO: handle back button and prevent back when calendar is open
    } else {
      // on non-mobile
      this.elements.input.removeAttribute("readonly");
    }
  }
  #registerEventListener() {
    this.elements.input.addEventListener("beforeinput", this.#onInputBeforeInput.bind(this));
    listenAndSilentEvent(this.elements.input, "focus", this.#onInputFocus.bind(this), { passive: true });
    listenAndSilentEvent(this.elements.input, "blur", this.#onInputBlur.bind(this), { passive: true });
    listenAndSilentEvent(this.elements.input, "keypress", this.#onInputKeyPress.bind(this));
    listenAndSilentEvent(this.elements.input, "keyup", this.#onInputKeyUp.bind(this));
    listenAndSilentEvent(this.elements.input, "keydown", this.#onInputKeyDown.bind(this));

    //
    this.elements.calendarTriggerButton.addEventListener("focus", this.#onCalendarButtonFocus.bind(this));
    this.elements.calendarTriggerButton.addEventListener("blur", this.#onCalendarButtonBlur.bind(this));
    this.elements.calendarTriggerButton.addEventListener("click", this.#onCalendarButtonClick.bind(this));
    //
    this.elements.calendar.addEventListener("select", e => this.#onCalendarSelect(e as CustomEvent));
    this.elements.calendar.addEventListener("init", this.#onCalendarElementInitiated.bind(this), {once:true});
    this.elements.calendar.addEventListener("blur", this.#onCalendarBlur.bind(this), { passive: true });
    this.elements.popover.addEventListener("close", this.#onPopoverClose.bind(this), { passive: true });
  }
  //true if all sub component initiated
  #isAllSubComponentInitiated = false;
  /**
   * @description wait for all sub-component to be load
   */
  async #waitForComponentsLoad() {
    if (this.#isAllSubComponentInitiated) {
      return Promise.resolve();
    }
    await customElements.whenDefined("jb-input");
    await customElements.whenDefined("jb-calendar");
    await customElements.whenDefined("jb-popover");
    customElements.upgrade(this.elements.input);
    customElements.upgrade(this.elements.calendar);
    customElements.upgrade(this.elements.popover);
    this.#isAllSubComponentInitiated = true;
    return Promise.resolve();
  }
  async #initProp() {
    await this.#waitForComponentsLoad();
    const valueAttribute = this.getAttribute("value");
    if (valueAttribute !== null) {
      this.value = valueAttribute;
    }
    this.#unsubscribeLocaleChange?.();
    if (this.hasAttribute("input-type")) this.#hasInputTypeOverride = true;
    if (this.hasAttribute("show-persian-number")) this.#hasShowPersianNumberOverride = true;
    if (!this.#hasInputTypeOverride) this.#setInputType(i18n.locale.calendar === "persian" ? InputTypes.jalali : InputTypes.gregorian);
    if (!this.#hasShowPersianNumberOverride) this.#setShowPersianNumber(i18n.locale.numberingSystem === "arabext");
    this.#unsubscribeLocaleChange = i18n.subscribe(() => {
      if (!this.#hasInputTypeOverride) this.#setInputType(i18n.locale.calendar === "persian" ? InputTypes.jalali : InputTypes.gregorian);
      if (!this.#hasShowPersianNumberOverride) this.#setShowPersianNumber(i18n.locale.numberingSystem === "arabext");
    });
    this.#callOnInitEvent();
  }
  static get dateInputObservedAttributes() {
    return ["value-type", "value", "name", "format", "min", "max", "required", "input-type", "dir", "show-persian-number", "placeholder", "disabled", "error"];
  }
  static get observedAttributes() {
    return [...JBInputWebComponent.observedAttributes, ...JBDateInputWebComponent.dateInputObservedAttributes];
  }
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) {
      return;
    }
    if (JBDateInputWebComponent.dateInputObservedAttributes.includes(name)) {
      this.#onAttributeChange(name, newValue);
    } else if (JBInputWebComponent.observedAttributes.includes(name)) {
      if (newValue === null) {
        this.elements.input.removeAttribute(name);
      } else {
        this.elements.input.setAttribute(name, newValue);
      }
    }
    // do something when an attribute has changed
  }
  #onAttributeChange(name: string, value: string | null) {
    switch (name) {
      case "value":
        this.value = value;
        break;
      case "name":
        if (value === null) {
          this.elements.input.removeAttribute("name");
        } else {
          this.elements.input.setAttribute("name", value);
        }
        break;
      case "value-type":
        if (value !== null) {
          this.valueType = value as ValueTypes;
        }
        break;
      case "format":
        if (value !== null) {
          this.setFormat(value);
        }
        break;
      case "min":
        this.#setMinDate(value);
        break;
      case "max":
        this.#setMaxDate(value);
        break;
      case "required":
        this.required = parseBooleanAttribute(value, false);
        break;
      case "input-type":
        this.#hasInputTypeOverride = value !== null;
        this.#setInputType(value === null ? (i18n.locale.calendar === "persian" ? InputTypes.jalali : InputTypes.gregorian) : (value as InputTypes));
        break;
      case "dir":
        this.#refreshCalendarDirection();
        break;
      case "show-persian-number":
        this.#hasShowPersianNumberOverride = value !== null;
        this.#setShowPersianNumber(parseBooleanAttribute(value, i18n.locale.numberingSystem === "arabext"));
        break;
      case "placeholder":
        this.placeholder = value;
        break;
      case "disabled":
        this.disabled = parseBooleanAttribute(value, false);
        break;
      case "error":
        this.reportValidity();
        break;
    }
  }
  /**
   * set the date value string format for parsing and converting.
   * @param newFormat
   */
  setFormat(newFormat: string) {
    //override new format base on user config
    this.#dateFactory.valueFormat = newFormat;
    //if we have min and max  date settled before format set we set them again so it works
    const minDate = this.getAttribute("min");
    if (minDate) {
      this.#setMinDate(minDate);
    }
    const maxDate = this.getAttribute("max");
    if (maxDate) {
      this.#setMaxDate(maxDate);
    }
  }
  setMinDate(minDate: string | Date | null) {
    this.#setMinDate(minDate);
  }
  #setMinDate(dateInput: string | Date | null) {
    if (dateInput === null) {
      this.dateRestrictions.min = null;
      if (this.elements.calendar.dateRestrictions) {
        this.elements.calendar.dateRestrictions.min = null;
      }
      return;
    }
    let minDate: Date | null = null;
    //create min date base on input value type
    if (typeof dateInput == "string") {
      minDate = this.#dateFactory.getDateFromValueDateString(dateInput);
    } else {
      minDate = dateInput;
    }
    if (minDate) {
      this.dateRestrictions.min = minDate;
      if (this.elements.calendar.dateRestrictions) {
        this.elements.calendar.dateRestrictions.min = minDate;
      }
    } else {
      console.error(`min date ${dateInput} is not valid and it will be ignored`, "\n", `please provide min date in format : ${this.#dateFactory.valueFormat}`);
    }
  }
  setMaxDate(maxDate: string | Date | null) {
    this.#setMaxDate(maxDate);
  }
  #setMaxDate(dateInput: string | Date | null) {
    if (dateInput === null) {
      this.dateRestrictions.max = null;
      if (this.elements.calendar.dateRestrictions) {
        this.elements.calendar.dateRestrictions.max = null;
      }
      return;
    }
    let maxDate: Date | null = null;
    //create max date base on input value type
    if (typeof dateInput == "string") {
      maxDate = this.#dateFactory.getDateFromValueDateString(dateInput);
    } else {
      maxDate = dateInput;
    }
    if (maxDate) {
      this.dateRestrictions.max = maxDate;
      if (this.elements.calendar.dateRestrictions) {
        this.elements.calendar.dateRestrictions.max = maxDate;
      }
    } else {
      console.error(`max date ${dateInput} is not valid and it will be ignored`, "\n", `please provide max date in format : ${this.#dateFactory.valueFormat}`);
    }
  }
  /**
   * this event generate by ourself in "onInputBeforeInput" after input done
   */
  #onInputInput(e: InputEvent) {
    this.#dispatchOnInputEvent(e);
  }
  #dispatchOnInputEvent(e: InputEvent): void {
    const event = createInputEvent("input", e, { cancelable: false });
    this.dispatchEvent(event);
  }
  #dispatchBeforeInputEvent(e: InputEvent): boolean {
    e.stopPropagation();
    const event = createInputEvent("beforeinput", e, { cancelable: true });
    this.dispatchEvent(event);
    if (event.defaultPrevented) {
      e.preventDefault();
    }
    return event.defaultPrevented;
  }
  #onInputBeforeInput(e: InputEvent) {
    const isPrevented = this.#dispatchBeforeInputEvent(e);
    if (isPrevented) {
      return;
    }

    const target = e.target as JBInputWebComponent;
    const previousInputValue = this.#sInputValue;
    //check if we are in placeholder mode we update or input text to standard mode
    if (e.data) {
      if (this.placeholder && target.value === "") {
        this.#inputValue = emptyInputValueString;
        target.setSelectionRange(0, 0);
      }
    }

    const beforeInputRes = handleBeforeInput({
      dateInputType: this.#dateFactory.inputType,
      showPersianNumber: this.showPersianNumber,
      value: this.#inputValue,
      selection: {
        start: target.selectionStart ?? 0,
        end: target.selectionEnd ?? target.selectionStart ?? 0,
      },
      event: {
        data: e.data,
        inputType: e.inputType,
      },
    });
    e.preventDefault();
    this.#inputValue = beforeInputRes.value;
    target.setSelectionRange(beforeInputRes.selectionStart, beforeInputRes.selectionEnd);
    //show placeholder if input were empty
    if (target.value == emptyInputValueString) {
      this.#clearValue();
    }
    if (this.#sInputValue !== previousInputValue) {
      this.#isDirty = true;
    }
    //because we preventDefault before input input will never be called so have to call it after we manually input all chars
    //TODO: make it cancellable
    this.#onInputInput(e);
  }
  #onInputKeyPress(e: KeyboardEvent) {
    e.stopPropagation();
    const keyPressEvent = createKeyboardEvent("keypress", e, { cancelable: false });
    this.dispatchEvent(keyPressEvent);
  }
  #onInputKeyUp(e: KeyboardEvent) {
    this.#updateValueFromInputString(this.#sInputValue);
    this.#dispatchOnInputKeyup(e);
  }
  #dispatchOnInputKeyup(e: KeyboardEvent) {
    e.stopPropagation();
    const event = createKeyboardEvent("keyup", e, { cancelable: false });
    this.dispatchEvent(event);
  }
  #onInputKeyDown(e: KeyboardEvent) {
    const notCancelled = this.#dispatchKeyDownEvent(e);
    if (!notCancelled) {
      e.preventDefault();
      return;
    }
    const target = e.target as JBInputWebComponent;
    if (e.keyCode == 38 || e.keyCode == 40) {
      //up and down button
      this.#isDirty = true;
      const caretPos = target.selectionStart!;
      if (caretPos < 5) {
        e.keyCode == 38 ? this.#addYear(1) : this.#addYear(-1);
        target.setSelectionRange(0, 4);
      }
      if (caretPos > 4 && caretPos < 8) {
        e.keyCode == 38 ? this.#addMonth(1) : this.#addMonth(-1);
        target.setSelectionRange(5, 7);
      }
      if (caretPos > 7) {
        e.keyCode == 38 ? this.#addDay(1) : this.#addDay(-1);
        target.setSelectionRange(8, 10);
      }
      e.preventDefault();
    }
  }
  #dispatchKeyDownEvent(e: KeyboardEvent) {
    e.stopPropagation();
    const event = createKeyboardEvent("keydown", e, { cancelable: false });
    return this.dispatchEvent(event);
  }
  #addYear(interval: number) {
    const currentYear = this.yearDisplayValue ? this.yearDisplayValue : this.#dateFactory.yearOnEmptyBaseOnInputType;
    const currentMonth = this.monthDisplayValue || 1;
    const currentDay = this.dayDisplayValue || 1;
    const { hour, minute, millisecond, second } = this.#valueObject.time;
    this.#setDateValueFromNumberBaseOnInputType(
      currentYear + interval,
      currentMonth,
      currentDay,
      hour ?? undefined,
      minute ?? undefined,
      second ?? undefined,
      millisecond ?? undefined,
    );
    this.#updateInputTextFromValue();
  }
  #addMonth(interval: number) {
    const currentYear = this.yearDisplayValue ? this.yearDisplayValue : this.#dateFactory.yearOnEmptyBaseOnInputType;
    const currentMonth = this.monthDisplayValue || 1;
    const currentDay = this.dayDisplayValue || 1;
    const { hour, minute, millisecond, second } = this.#valueObject.time;
    this.#setDateValueFromNumberBaseOnInputType(
      currentYear,
      currentMonth + interval,
      currentDay,
      hour ?? undefined,
      minute ?? undefined,
      second ?? undefined,
      millisecond ?? undefined,
    );
    this.#updateInputTextFromValue();
  }
  #addDay(interval: number) {
    const currentYear = this.yearDisplayValue ? this.yearDisplayValue : this.#dateFactory.yearOnEmptyBaseOnInputType;
    const currentMonth = this.monthDisplayValue || 1;
    const currentDay = this.dayDisplayValue || 1;
    const { hour, minute, millisecond, second } = this.#valueObject.time;
    this.#setDateValueFromNumberBaseOnInputType(
      currentYear,
      currentMonth,
      currentDay + interval,
      hour ?? undefined,
      minute ?? undefined,
      second ?? undefined,
      millisecond ?? undefined,
    );
    this.#updateInputTextFromValue();
  }
  /**
   * @description will convert current valueObject to expected value string
   */
  getDateValue(type: ValueType = this.valueType): string {
    return this.#dateFactory.getDateValueStringFromValueObject(this.#valueObject, type);
  }
  /**
   * @description when user change value this function called and update inner value object base on user value
   */
  #setDateValue(value: string | Date | null) {
    if (value === null || value === "") {
      this.#clearValue();
    } else if (typeof value == "string") {
      switch (this.#dateFactory.valueType) {
        case "GREGORIAN":
        case "JALALI":
          this.#setDateValueFromString(value);
          break;
        case "TIME_STAMP":
          this.#setDateValueFromTimeStamp(value);
          break;
      }
    } else if (value instanceof Date) {
      this.#setDateValueFromDate(value);
    }
    this.#updateFormValue();
  }
  /**
   * called when we want empty null value (not initial value)
   */
  #clearValue() {
    // mean we reset calendar value and set it to null
    this.#valueObject = getEmptyValueObject();
    if (this.elements.calendar.value?.day) {
      this.elements.calendar.value = null;
    }
    if (this.placeholder) {
      this.#inputValue = "";
    }
  }
  #updateCalendarView() {
    if(this.elements.calendar.isConnected){
      this.elements.calendar.addEventListener("init",()=>{
        this.#updateCalendarView();
      },{once:true})
    }
    //update jb-calendar view base on current data
    const value: JBCalendarValue = {
      year: this.#dateFactory.getCalendarYear(this.#valueObject),
      month: this.#dateFactory.getCalendarMonth(this.#valueObject),
      day: this.#dateFactory.getCalendarDay(this.#valueObject),
    };
    if (value.year && value.month && value.day) {
      //if we have all data we update calendar value
      this.elements.calendar.value = value;
    } else if (value.year && value.month) {
      //if we don't have all data we just set view year and month
      this.elements.calendar.data.selectedYear = value.year;
      this.elements.calendar.data.selectedMonth = value.month;
    }
  }
  /**
   * @description set date value from javascript Date
   */
  #setDateValueFromDate(value: Date | null) {
    let valueObject = getEmptyValueObject();
    if (value) {
      valueObject = this.#dateFactory.getDateObjectValueFromDateValue(value);
    }
    this.#valueObject = valueObject;
    this.#updateCalendarView();
  }
  /**
   * @description set date value from timestamp base on valueType
   */
  #setDateValueFromTimeStamp(value: string) {
    const timeStamp = Number(value);
    this.#valueObject = this.#dateFactory.getDateValueObjectFromTimeStamp(timeStamp);
    this.#updateCalendarView();
  }
  /**
   * @description set date value from string base on valueType
   */
  #setDateValueFromString(value: string) {
    const dateInObject = this.#dateFactory.getDateObjectValueBaseOnFormat(value);

    if (dateInObject.year && dateInObject.month && dateInObject.day) {
      this.#setDateValueFromNumbers(
        Number(dateInObject.year),
        Number(dateInObject.month),
        Number(dateInObject.day),
        Number(dateInObject.hour ?? "00"),
        Number(dateInObject.minute ?? "00"),
        Number(dateInObject.second ?? "00"),
        Number(dateInObject.millisecond ?? "000"),
      );
    } else {
      if (value !== null && value !== undefined && value !== "") {
        console.error("your inputted Date doest match default or your specified Format");
      } else {
        this.#clearValue();
      }
    }
  }
  /**
   * @description set value object base on currently valueType
   */
  #setDateValueFromNumbers(year: number, month: number, day: number, hour?: number, minute?: number, second?: number, millisecond?: number) {
    const prevYear = this.yearValue;
    const prevMonth = this.monthValue;
    const result: JBDateInputValueObject = this.#dateFactory.getDateValueObjectBaseOnValueType(year, month, day, prevYear, prevMonth, hour, minute, second, millisecond);
    this.#valueObject = result;
    this.#updateCalendarView();
  }
  /**
   * set value object base on currently inputType (call this function when date is complete)
   * @param {number} year jalali or gregorian year
   * @param {number} month jalali or gregorian month
   * @param {number} day jalali or gregorian day
   */
  #setDateValueFromNumberBaseOnInputType(
    year: number,
    month: number,
    day: number,
    hour?: number | null,
    minute?: number | null,
    second?: number | null,
    millisecond?: number | null,
  ) {
    //TODO: refactor this component to use Temporal value as a core object
    const prevYear = this.yearBaseOnInputType;
    const prevMonth = this.monthBaseOnInputType;
    const result: JBDateInputValueObject = this.#dateFactory.getDateValueObjectBaseOnInputType(year, month, day, prevYear, prevMonth, hour, minute, second, millisecond);
    this.#valueObject = result;
    this.#updateCalendarView();
    this.#updateFormValue();
  }
  #updateInputTextFromValue() {
    const { year, month, day } = this.inputType == InputTypes.jalali ? this.#valueObject.jalali : this.#valueObject.gregorian;
    if (this.placeholder && !(year && month && day)) {
      //if we have placeholder and inputted value were all null we show placeholder until user input some value
      this.#inputValue = "";
      return;
    }
    //
    let str = inputFormat;
    let yearString = "    ",
      monthString = "  ",
      dayString = "  ";
    if (year != null && !Number.isNaN(year)) {
      if (year < 10) {
        yearString = `000${year}`;
      } else if (year < 100) {
        yearString = `00${year}`;
      } else if (year < 1000) {
        yearString = `0${year}`;
      } else {
        yearString = year.toString();
      }
    }
    if (month != null && !Number.isNaN(month)) {
      if (month < 10) {
        monthString = `0${month}`;
      } else {
        monthString = month.toString();
      }
    }
    if (day != null && !Number.isNaN(day)) {
      if (day < 10) {
        dayString = `0${day}`;
      } else {
        dayString = day.toString();
      }
    }
    //convert to fa char if needed
    if (this.#showPersianNumber) {
      yearString = enToFaDigits(yearString);
      monthString = enToFaDigits(monthString);
      dayString = enToFaDigits(dayString);
    }
    str = str.replace("YYYY", yearString).replace("MM", monthString).replace("DD", dayString);
    this.#inputValue = str;
  }
  /**
   * called when input text change and we want to update value object base on input text
   * @param {string}inputString
   */
  #updateValueFromInputString(inputString: string) {
    inputRegex.lastIndex = 0;
    const res = inputRegex.exec(inputString);
    if (res?.groups) {
      //TODO: update this when support Temporal time and get times factor from input
      const { hour, minute, millisecond, second } = this.#valueObject.time;
      const year = Number(res.groups.year);
      const month = Number(res.groups.month);
      const day = Number(res.groups.day);
      if (year && month && day) {
        this.#setDateValueFromNumberBaseOnInputType(year, month, day, hour, minute, second, millisecond);
      }
    }
  }
  /**
   * @public
   * @description focus on date input web-component
   */
  focus() {
    //public
    this.elements.input.focus();
    this.isOpen = true;
  }
  #handleCaretPosOnInputFocus() {
    const newCaretPos = getFixedCaretPos({ inputValue: this.displayValue, selectionStart: this.elements.input.selectionStart });
    if (newCaretPos !== null) {
      if (newCaretPos !== this.elements.input.selectionStart) {
        this.elements.input.setSelectionRange(newCaretPos, newCaretPos);
      }
    }
    const caretPos = newCaretPos ?? this.elements.input.selectionStart ?? 0;
    if (this.#isCalendarDefaultDateView && !this.typedYear.trim() && !this.typedMonth.trim() && !this.typedDay.trim()) {
      //when user set calendar default year and month we ask user to select day
      this.elements.calendar.activeSection = "DAY";
      return;
    }
    const selectionPart = getSelectionPart(caretPos);
    if (selectionPart) {
      this.elements.calendar.activeSection = selectionPart;
    }
  }
  #onDocumentSelectionChange = () => this.#handleCaretPosOnInputFocus();
  #lastInputStringValue = emptyInputValueString;
  /**
   * check if there is no update from last time then if change we update. remember to call returned update.
   * @param { string }newString newly typed String
   */
  #checkIfInputTextIsChangedFromLastTime(newString: string) {
    const updatePrevValue = () => {
      this.#lastInputStringValue = newString;
    };
    if (this.#lastInputStringValue != newString) {
      this.#lastInputStringValue = newString;
      return { isUpdated: true, updatePrevValue };
    }
    return { isUpdated: false, updatePrevValue };
  }
  #onInputFocus(e: FocusEvent) {
    this.#lastInputStringValue = this.#sInputValue;
    this.focus();
    //don't add once:true here because we need to detect every caret pos change during the type and then r r input on blur
    document.addEventListener("selectionchange", this.#onDocumentSelectionChange);
    this.#dispatchFocusEvent(e);
  }
  #dispatchFocusEvent(e: FocusEvent) {
    e.stopPropagation();
    const event = createFocusEvent("focus", e, { cancelable: false });
    this.dispatchEvent(event);
  }
  #onInputBlur(e: FocusEvent) {
    document.removeEventListener("selectionchange", this.#onDocumentSelectionChange);
    const focusedElement = e.relatedTarget;
    if (focusedElement !== this.elements.calendar && focusedElement !== this.elements.calendarTriggerButton) {
      this.isOpen = false;
    }
    const inputText = this.#sInputValue;
    //check if there is no update from last time then if change we update
    const changeTestRes = this.#checkIfInputTextIsChangedFromLastTime(inputText);
    if (changeTestRes.isUpdated) {
      this.#updateValueFromInputString(inputText);
      const dispatchedEvent = this.#dispatchOnChangeEvent();
      this.#checkValidity(true);
      if (dispatchedEvent.defaultPrevented) {
        e.preventDefault();
        this.#updateValueFromInputString(this.#lastInputStringValue);
      } else {
        changeTestRes.updatePrevValue();
      }
    }
    this.#dispatchBlurEvent(e);
  }
  #dispatchBlurEvent(e: FocusEvent) {
    e.stopPropagation();
    const event = createFocusEvent("blur", e, { cancelable: false });
    this.dispatchEvent(event);
  }
  #onCalendarBlur(e: FocusEvent) {
    const focusedElements = e.composedPath();
    if (!focusedElements.includes(this.elements.input) && !focusedElements.includes(this.elements.calendarTriggerButton) && !focusedElements.includes(this.elements.calendar)) {
      this.isOpen = false;
    }
  }
  #onPopoverClose() {
    this.isOpen = false;
    this.elements.input.blur();
  }
  #dispatchOnChangeEvent() {
    const event = new Event("change", { composed: true, bubbles: true, cancelable: true });
    this.dispatchEvent(event);
    return event;
  }
  #getInsideValidations() {
    const validationList: ValidationItem<ValidationValue>[] = [];
    if (this.getAttribute("error") !== null && (this.getAttribute("error") ?? "").trim().length > 0) {
      validationList.push({
        validator: undefined,
        message: this.getAttribute("error")!,
        stateType: "customError",
      });
    }
    if (this.required) {
      validationList.push(requiredValidation);
    }
    if (this.dateRestrictions.min) {
      validationList.push({
        validator: value => {
          return checkMinValidation(new Date(value.valueObject.timeStamp!), this.dateRestrictions.min!);
        },
        message: dictionary.get(i18n, "minRangeViolation"),
        stateType: "rangeUnderflow",
      });
    }
    if (this.dateRestrictions.max) {
      validationList.push({
        validator: value => {
          return checkMaxValidation(new Date(value.valueObject.timeStamp!), this.dateRestrictions.max!);
        },
        message: dictionary.get(i18n, "maxRangeViolation"),
        stateType: "rangeOverflow",
      });
    }

    return validationList;
  }
  showValidationError(error: ShowValidationErrorParameters) {
    this.elements.input.showValidationError(error);
    this.#internals?.states?.add("invalid");
    if (this.#internals) this.#internals.ariaInvalid = "true";
  }
  clearValidationError() {
    this.elements.input?.clearValidationError?.();
    this.#internals?.states?.delete("invalid");
    if (this.#internals) this.#internals.ariaInvalid = "false";
  }
  #onCalendarElementInitiated() {
    this.elements.calendar.dateRestrictions.min = this.dateRestrictions.min;
    this.elements.calendar.dateRestrictions.max = this.dateRestrictions.max;
    this.elements.calendar.defaultCalendarData = {
      gregorian: {
        year: this.#dateFactory.nicheNumbers.calendarYearOnEmpty.gregorian,
        month: this.#dateFactory.nicheNumbers.calendarMonthOnEmpty.gregorian,
      },
      jalali: {
        year: this.#dateFactory.nicheNumbers.calendarYearOnEmpty.jalali,
        month: this.#dateFactory.nicheNumbers.calendarMonthOnEmpty.jalali,
      },
    };
    this.#updateCalendarView();
  }
  #isCalendarButtonClickEventIsAfterFocusEvent = false;
  #onCalendarButtonFocus(e: FocusEvent) {
    const prevFocused = e.relatedTarget;
    if (this.isOpen && prevFocused && [this.elements.calendar as EventTarget, this.elements.input as EventTarget].includes(prevFocused)) {
      //if calendar was displayed but user click on icon we hide it here
      (prevFocused as HTMLInputElement).focus();
      this.isOpen = false;
    } else {
      // if user focus on calendar button from outside of calendar area we show calendar
      this.#isCalendarButtonClickEventIsAfterFocusEvent = true;
      this.isOpen = true;
    }
  }
  #onCalendarButtonBlur(e: FocusEvent) {
    if (![this.elements.calendar as EventTarget, this.elements.input as EventTarget].includes(e.relatedTarget!)) {
      this.isOpen = false;
    }
  }
  #onCalendarButtonClick() {
    const focusedElement = this.shadowRoot?.activeElement;
    if (focusedElement && !this.#isCalendarButtonClickEventIsAfterFocusEvent && focusedElement == this.elements.calendarTriggerButton) {
      //check if this click is event exactly after focus or not if its after focus we just pass but if its not and its a second click we close menu or reopen menu if closed before
      this.isOpen = !this.isOpen;
    }
    this.#isCalendarButtonClickEventIsAfterFocusEvent = false;
  }
  #onCalendarSelect(e: CustomEvent) {
    const target = e.target as JBCalendarWebComponent;
    const { year, month, day } = target.value;
    if (year && month && day) {
      const prevValueDate = structuredClone(this.valueAsDate);
      const wasDirty = this.#isDirty;
      this.#isDirty = true;
      const { hour, minute, millisecond, second } = this.#valueObject.time;
      this.#setDateValueFromNumberBaseOnInputType(year, month, day, hour, minute, second, millisecond);
      this.#updateInputTextFromValue();
      this.isOpen = false;
      this.#callOnDateSelect();
      this.#checkValidity(true);
      const dispatchedEvent = this.#dispatchOnChangeEvent();
      if (dispatchedEvent.defaultPrevented) {
        e.preventDefault();
        this.#setDateValueFromDate(prevValueDate);
        this.#updateInputTextFromValue();
        this.#isDirty = wasDirty;
      }
    }
  }
  #callOnDateSelect() {
    //when user pick a day in calendar modal
    const event = new CustomEvent("select");
    this.dispatchEvent(event);
  }
  async onInputTypeChange() {
    //wait for sub-component load on first value initiation
    if (!this.#isAllSubComponentInitiated) {
      await this.#waitForComponentsLoad();
    }
    this.elements.calendar.value = {
      year: this.#dateFactory.getCalendarYear(this.#valueObject),
      month: this.#dateFactory.getCalendarMonth(this.#valueObject),
      day: this.#dateFactory.getCalendarDay(this.#valueObject),
    }
    this.elements.calendar.inputType = this.inputType;
    this.#updateInputTextFromValue();
  }
  /**
   * needed for ui logic if user set calendar default value (used in input selection change)
   */
  #isCalendarDefaultDateView = false;
  /**
   * set opened calendar date when date input value is empty
   * @public
   * @param  year which year you want to show in empty state in calendar.
   * @param  month which month you want to show in empty state in calendar.
   * @param  dateType default is your configured input-type  but you can set it otherwise if you want to change other type of calendar in case of change in input-type.
   */
  setCalendarDefaultDateView(year: number, month: number, dateType: InputType | undefined) {
    if (year && month) {
      this.#isCalendarDefaultDateView = true;
      this.#dateFactory.setCalendarDefaultDateView(year, month, dateType);
      this.#updateCalendarView();
    }
  }
  #checkValidity(showError: boolean) {
    if (!this.isAutoValidationDisabled) {
      if (this.#internals?.states.has("invalid")) {
        // if we currently showing error to user it make sure error get updated (when failed validation changed of function return different string as an error)
        showError = true;
      }
      return this.#validation.checkValidity({ showError });
    }
  }
  /**
   * @public
   * @description this method used to check for validity but doesn't show error to user and just return the result
   * this method used by #internal of component
   */
  checkValidity(): boolean {
    const validationResult = this.#validation.checkValiditySync({ showError: false });
    if (!validationResult.isAllValid) {
      this.#dispatchInvalidEvent();
    }
    return validationResult.isAllValid;
  }
  /**
   * @public
   * @description this method used to check for validity and show error to user
   */
  reportValidity(): boolean {
    const validationResult = this.#validation.checkValiditySync({ showError: true });
    if (!validationResult.isAllValid) {
      this.#dispatchInvalidEvent();
    }
    return validationResult.isAllValid;
  }
  #dispatchInvalidEvent() {
    const event = new CustomEvent("invalid");
    this.dispatchEvent(event);
  }
  /**
   * @description this method called on every checkValidity calls and update validation result of #internal
   */
  #setValidationResult(result: ValidationResult<ValidationValue>) {
    if (result.isAllValid) {
      this.#internals?.setValidity({}, "");
    } else {
      const states: ValidityStateFlags = {};
      let message = "";
      result.validationList.forEach(res => {
        if (!res.isValid) {
          if (res.validation.stateType) {
            states[res.validation.stateType] = true;
          } else {
            states["customError"] = true;
          }
          if (message == "") {
            message = res.message ?? "";
          }
        }
      });
      this.#internals?.setValidity(states, message);
    }
  }
  get validationMessage() {
    return this.#internals!.validationMessage;
  }
  get validity() {
    return this.#internals?.validity;
  }
}
defineWebComponent("jb-date-input", JBDateInputWebComponent);

declare global {
  interface HTMLElementTagNameMap {
    "jb-date-input": JBDateInputWebComponent;
  }
}
